<template>
    <div>
        <div class="row gutter-xs">
            <div class="panel panel-default">
                <div class="panel-body">
                    <div class="flex flex-col items-center justify-between md:flex-row">
                        <div class="hidden lg:block lg:w-1/3"></div>
                        <div class="flex justify-center lg:justify-center md:justify-end md:w-1/3 w-full">
                            <div class="form-group form-group-sm">
                                <div class="input-with-icon mx-auto w-300px">
                                    <input class="form-control input-sm" type="search" v-model="searchQuery" :placeholder="$t('communication.kiosk_search_placeholder')">
                                    <span class="icon icon-search input-icon"></span>
                                </div>
                            </div>
                        </div>
                        <div class="hidden lg:block lg:w-1/3"></div>
                    </div>
                    <vue-good-table
                        mode="remote"
                        @on-page-change="onPageChange"
                        @on-sort-change="onSortChange"
                        @on-per-page-change="onPerPageChange"
                        @on-search="onSearchChange"
                        :search-options="{ enabled: true, externalQuery: serverParams.search }"
                        :sort-options="{
                            enabled: true,
                            initialSortBy: { field: 'created_at', type: 'desc' },
                        }"
                        :pagination-options="{
                            enabled: true,
                            perPage: 20,
                            setCurrentPage: table.currentPage,
                            nextLabel: $t('tables.next'),
                            prevLabel: $t('tables.previous'),
                            rowsPerPageLabel: $t('tables.rows_per_page'),
                            ofLabel: $t('tables.of'),
                            pageLabel: $t('tables.page'),
                            allLabel: $t('tables.all'),
                        }"
                        :columns="table.columns"
                        :rows="table.rows"
                        :total-rows="table.totalRecords"
                        :is-loading="table.isLoading"
                        style-class="table vgt-table striped">
                        <div slot="emptystate">
                            <div class="row text-center p-y-md">
                                <div class="col-xs-8 col-xs-offset-2">
                                    <p>{{ $t('tables.no_records_found') }}</p>
                                </div>
                            </div>
                        </div>
                        <template slot="table-row" slot-scope="props">
                            <template v-if="props.column.field === 'notification'">
                                {{ props.row.notification.replace('App\\Notifications\\', '') }}
                            </template>
                            <template v-else-if="props.column.field === 'recipient'">
                                <template v-if="props.row.user">
                                    {{ props.row.user.email }}
                                </template>
                                <template v-else>
                                    {{ props.row.notifiable_routes.mail }}
                                </template>
                            </template>
                            <template v-else-if="props.column.field === 'organization'">
                                <div v-if="props.row.organization" class="flex items-center">
                                    <div class="mr-3">
                                        <organization-logo :organization="props.row.organization" :size="'32px'"></organization-logo>
                                    </div>
                                    <div class="" v-html="props.row.organization.name"></div>
                                </div>
                                <template v-else>
                                    {{ $t('globals.n_a') }}
                                </template>
                            </template>
                            <template v-else-if="props.column.field === 'created_at'">
                                <div class="flex flex-col">
                                    <div>
                                        {{ formatDate(props.row.created_at) }}
                                    </div>
                                    <div class="font-medium text-2xs text-gray-table-light">
                                        ({{ props.row.readable_created_at }})
                                    </div>
                                </div>
                            </template>
                            <template v-else-if="props.column.field == 'actions'">
                                <div class="basic-btn-toolbar btn-toolbar flex justify-center" role="toolbar">
                                    <div class="btn-group btn-group-xs" role="group">
                                        <a :href="`/kiosk/communication/${props.row.id}/preview`" target="_blank" class="btn btn-default plain" v-tooltip="{ content: $t('communication.preview_in_a_new_tab'), placement: 'top' }">
                                            <span class="icon icon-eye"></span>
                                        </a>
                                    </div>
                                </div>
                            </template>
                            <span v-else v-html="props.formattedRow[props.column.field]"></span>
                        </template>
                    </vue-good-table>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { lightFormat, parseISO } from 'date-fns';
import OrganizationLogo from '../../user/partials/organization-logo.vue';

export default {
    components: {
        'organization-logo': OrganizationLogo,
    },

    props: {
        initialSort: {
            type: Array,
            default: function() {
                return [
                    { column: 'created_at', dir: 'desc' },
                ];
            },
        },
    },

    data() {
        return {
            table: {
                columns: [
                    {
                        label: this.$t('communication.notification'),
                        field: 'notification',
                        sortable: false,
                    },
                    {
                        label: this.$t('communication.recipient'),
                        field: 'recipient',
                    },
                    {
                        label: this.$t('globals.organization'),
                        field: 'organization',
                    },
                    {
                        label: this.$t('globals.sent_at'),
                        field: 'created_at',
                    },
                    {
                        label: this.$t('globals.actions'),
                        field: 'actions',
                        html: true,
                        sortable: false,
                        thClass: 'text-center',
                    },
                ],
                rows: [],
                totalRecords: 0,
                currentPage: 1,
                isLoading: false,
            },
            serverParams: {
                order: [],
                start: 0,
                length: 20,
                search: '',
            },
            searchQuery: '',
        };
    },

    watch: {
        searchQuery: Globals.debounce(function (value) {
            this.serverParams.search = value;
        }, 750)
    },

    methods: {
        updateParams(newProps) {
            this.serverParams = Object.assign({}, this.serverParams, newProps);
        },

        onPageChange(params) {
            this.table.currentPage = params.currentPage;
            let start = 0;
            if (params.currentPage > 1) {
                start = (params.currentPage - 1) * params.currentPerPage;
            }
            this.updateParams({ start: start });
            this.loadItems();
        },

        onPerPageChange(params) {
            this.updateParams({ length: params.currentPerPage });
            this.loadItems();
        },

        onSortChange(params) {
            const newOrder = [];
            newOrder.push({ 'column': _.head(params)['field'], 'dir': _.head(params)['type'] });
            this.updateParams({ order: newOrder });
            this.loadItems();
        },

        onSearchChange(params) {
            this.updateParams({ search: params.searchTerm });
            this.loadItems();
        },

        goToFirstPage() {
            this.table.currentPage = 1;
            this.updateParams({ start: 0 });
            this.loadItems();
        },

        loadItems() {
            this.table.isLoading = true;

            const queryParams = Object.assign({}, this.serverParams);

            axios.get('/data/kiosk/communication', { params: queryParams })
                .then(response => {
                    this.table.totalRecords = response.data.recordsFiltered;
                    this.table.rows = response.data.data;
                    this.table.isLoading = false;
                });
        },

        formatDate(date) {
            return lightFormat(parseISO(date), 'dd-MM-y HH:mm');
        },
    },
};
</script>
